<template>
  <div class="todo-container">
    <!-- Todo Logo -->
    <div class="todo-logo">
      <span class="diamond">◇</span>
      <span class="diamond">◇</span>
      <span class="todo-text">TODO</span>
      <span class="diamond">◇</span>
      <span class="diamond">!</span>
    </div>
    
    <!-- 任务输入框 -->
    <div class="input-wrapper">
      <input 
        v-model="newTask" 
        @keyup.enter="addTask"
        placeholder="新增待办事项..."
      />
      <button class="submit-btn" @click="addTask">提交</button>
    </div>

    <!-- 主要内容区 -->
    <div class="main-content">
      <!-- 任务列表区域 -->
      <div class="tasks-container">
        <!-- 状态栏 -->
        <div class="status-bar">
          <div class="status-text" :class="{ active: allCompleted }">
            <span v-if="tasks.length > 0">
              {{ completedCount }}/{{ tasks.length }} 已完成
            </span>
          </div>
          <div class="drag-hint">💡 拖拽任务可重新排序</div>
          <div class="slogan">今日事今日毕，勿将今事待明日!☕</div>
        </div>

        <!-- 任务列表 -->
        <div class="todo-list">
          <div v-for="task in filteredTasks" 
               :key="task.id" 
               class="todo-item"
               :class="{ completed: task.completed }"
               draggable="true"
               @dragstart="dragStart($event, task)"
               @dragover.prevent
               @drop="drop($event, task)">
            <input 
              type="checkbox" 
              :checked="task.completed"
              @change="toggleTask(task)"
            />
            <span class="task-content">{{ task.content }}</span>
            <input 
              type="date" 
              v-model="task.dueDate"
              @change="updateTask(task)"
              class="due-date-input"
            />
            <button @click="deleteTask(task)" class="delete-btn">×</button>
          </div>
          
          <!-- 空状态 -->
          <div v-if="filteredTasks.length === 0" class="empty-state">
            <p v-if="searchQuery">没有找到匹配的任务</p>
            <p v-else-if="!showCompleted && tasks.length > 0">所有任务都已完成！</p>
            <p v-else>还没有待办事项，开始添加吧！</p>
          </div>
        </div>
      </div>

      <!-- 右侧快捷操作 -->
      <div class="quick-actions">
        <button class="action-btn primary" @click="showQuickActions = !showQuickActions">
          {{ showQuickActions ? '关' : '开' }}✨
        </button>
        <div v-show="showQuickActions" class="action-buttons">
          <button class="action-btn" @click="toggleShowCompleted">
            {{ showCompleted ? '隐藏已完成' : '显示全部' }}
          </button>
          <button class="action-btn" @click="clearAllTasks">清除全部</button>
          <button class="action-btn" @click="exportTasks">导出数据</button>
          <button class="action-btn" @click="importTasks">导入数据</button>
          <button class="action-btn" @click="manualPush">数据同步</button>
        </div>
        <!-- 搜索框 -->
        <input 
          v-model="searchQuery" 
          placeholder="搜索任务..."
          class="search-input"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { syncTodoData } from '@/api'

export default {
  name: 'Todo',
  data() {
    return {
      tasks: [],
      newTask: '',
      searchQuery: '',
      showCompleted: true,
      showQuickActions: true,
      isLogin: false,
      draggedTask: null
    }
  },
  computed: {
    filteredTasks() {
      return this.tasks
        .filter(task => {
          const matchesSearch = task.content.toLowerCase().includes(this.searchQuery.toLowerCase())
          return matchesSearch && (this.showCompleted || !task.completed)
        })
        .sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return 0
          if (!a.dueDate) return 1
          if (!b.dueDate) return -1
          return new Date(a.dueDate) - new Date(b.dueDate)
        })
    },
    allCompleted() {
      return this.tasks.length > 0 && this.tasks.every(task => task.completed)
    },
    completedCount() {
      return this.tasks.filter(task => task.completed).length
    }
  },
  mounted() {
    // 从localStorage加载任务
    const savedTasks = localStorage.getItem('todos')
    if (savedTasks) {
      this.tasks = JSON.parse(savedTasks)
    }

    // 检查登录状态
    this.checkLoginStatus()
  },
  methods: {
    checkLoginStatus() {
      const token = localStorage.getItem('token')
      const username = localStorage.getItem('username')
      this.isLogin = !!(token && username)
    },

    // 数据同步
    async manualPush() {
      try {
        // 检查登录状态
        if (!this.isLogin) {
          this.$message.error('请先登录')
          this.$router.push('/login')
          return
        }

        // 检查是否有任务需要同步
        if (this.tasks.length === 0) {
          this.$message.info('没有任务需要同步')
          return
        }

        // 推送所有任务数据用于同步
        const response = await syncTodoData({
          tasks: this.tasks.map(task => ({
            content: task.content,
            completed: task.completed,
            dueDate: task.dueDate,
            createDate: task.createDate
          }))
        })

        // 检查响应状态
        if (response && response.status === 200) {
          this.$message.success('数据同步成功')
        } else {
          this.$message.error((response && response.msg) || '数据同步失败')
        }
      } catch (error) {
        let errorMsg = '数据同步失败，请稍后重试'
        if (error.response && error.response.data && error.response.data.msg) {
          errorMsg = error.response.data.msg
        }
        this.$message.error(errorMsg)
      }
    },

    addTask() {
      if (!this.newTask.trim()) return

      const task = {
        id: Date.now().toString(),
        content: this.newTask,
        completed: false,
        createDate: new Date().toISOString(),
        dueDate: ''
      }

      this.tasks.push(task)
      this.newTask = ''
      this.saveTasks()
    },

    toggleTask(task) {
      task.completed = !task.completed
      this.saveTasks()
    },

    updateTask(task) {
      this.saveTasks()
    },

    deleteTask(task) {
      const index = this.tasks.indexOf(task)
      if (index > -1) {
        this.tasks.splice(index, 1)
        this.saveTasks()
      }
    },

    saveTasks() {
      localStorage.setItem('todos', JSON.stringify(this.tasks))
    },

    exportTasks() {
      if (this.tasks.length === 0) {
        this.$message.info('没有任务可以导出')
        return
      }

      // 准备导出数据，确保格式完整
      const exportData = this.tasks.map(task => ({
        id: task.id,
        content: task.content,
        completed: task.completed,
        createDate: task.createDate,
        dueDate: task.dueDate || ''
      }))

      const data = JSON.stringify(exportData, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      
      const a = document.createElement('a')
      a.href = url
      a.download = `todo-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      this.$message.success(`成功导出 ${this.tasks.length} 个任务`)
    },

    importTasks() {
      // 创建一个隐藏的文件输入元素
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.accept = '.json'
      fileInput.style.display = 'none'
      
      // 监听文件选择事件
      fileInput.addEventListener('change', (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        // 检查文件类型
        if (!file.name.endsWith('.json')) {
          this.$message.error('请选择JSON格式的文件')
          return
        }
        
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const importedTasks = JSON.parse(e.target.result)
            
            // 验证导入的数据格式
            if (Array.isArray(importedTasks) && importedTasks.every(task => 
              typeof task === 'object' && 
              'id' in task && 
              'content' in task && 
              'completed' in task
            )) {
              // 合并任务，避免重复
              const existingIds = new Set(this.tasks.map(t => t.id))
              const newTasks = importedTasks.filter(task => !existingIds.has(task.id))
              
              if (newTasks.length === 0) {
                this.$message.info('没有新的任务需要导入')
                return
              }
              
              this.tasks = [...this.tasks, ...newTasks]
              this.saveTasks()
              this.$message.success(`成功导入 ${newTasks.length} 个任务`)
            } else {
              this.$message.error('导入的数据格式不正确，请确保是有效的Todo任务数据')
            }
          } catch (error) {
            console.error('导入失败:', error)
            this.$message.error('导入失败，请确保文件是有效的JSON格式')
          }
        }
        
        reader.readAsText(file)
      })
      
      // 触发文件选择对话框
      document.body.appendChild(fileInput)
      fileInput.click()
      document.body.removeChild(fileInput)
    },

    toggleShowCompleted() {
      this.showCompleted = !this.showCompleted
    },

    clearAllTasks() {
      if (this.tasks.length === 0) {
        this.$message.info('没有任务需要清除')
        return
      }

      if (confirm('确定要清除所有任务吗？')) {
        this.tasks = []
        this.saveTasks()
        this.$message.success('已清除所有任务')
      }
    },

    // 拖拽功能
    dragStart(event, task) {
      this.draggedTask = task
      event.dataTransfer.effectAllowed = 'move'
      event.target.style.opacity = '0.5'
    },

    drop(event, targetTask) {
      event.preventDefault()
      
      if (!this.draggedTask || this.draggedTask.id === targetTask.id) {
        return
      }

      // 获取拖拽任务和目标任务的索引
      const draggedIndex = this.tasks.findIndex(task => task.id === this.draggedTask.id)
      const targetIndex = this.tasks.findIndex(task => task.id === targetTask.id)
      
      if (draggedIndex === -1 || targetIndex === -1) {
        return
      }

      // 重新排序任务
      const newTasks = [...this.tasks]
      const [draggedItem] = newTasks.splice(draggedIndex, 1)
      newTasks.splice(targetIndex, 0, draggedItem)
      
      this.tasks = newTasks
      this.saveTasks()
      
      // 重置拖拽状态
      this.draggedTask = null
      event.target.style.opacity = '1'
    }
  }
}
</script>

<style scoped>
.todo-container {
  max-width: 1200px;
  margin: 40px auto;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
}

.todo-logo {
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5em;
  letter-spacing: 2px;
}

.diamond {
  color: #666;
  margin: 0 4px;
}

.todo-text {
  font-family: 'Comic Sans MS', cursive;
  color: #333;
  font-weight: bold;
  letter-spacing: 4px;
}

.input-wrapper {
  position: relative;
  margin-bottom: 30px;
  display: flex;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.input-wrapper input {
  flex: 1;
  padding: 16px 20px;
  border: 2px solid #f0f0f0;
  border-right: none;
  font-size: 16px;
  background: white;
}

.input-wrapper input:focus {
  outline: none;
  border-color: #a8e6cf;
}

.submit-btn {
  padding: 0 30px;
  background-color: #ffd1dc;
  color: #333;
  border: none;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.submit-btn:hover {
  opacity: 0.9;
}

.main-content {
  display: flex;
  gap: 20px;
}

.tasks-container {
  flex: 1;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.status-bar {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  background: #a8e6cf;
  color: #333;
  gap: 15px;
}

.status-text {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  font-size: 14px;
}

.drag-hint {
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
}

.slogan {
  margin-left: auto;
  font-size: 14px;
}

.todo-list {
  padding: 20px;
}

.todo-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.2s;
  cursor: grab;
}

.todo-item:active {
  cursor: grabbing;
}

.todo-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.todo-item.completed {
  opacity: 0.7;
  background: #f0f0f0;
}

.todo-item input[type="checkbox"] {
  margin-right: 12px;
  width: 18px;
  height: 18px;
}

.task-content {
  flex: 1;
  margin-right: 12px;
}

.completed .task-content {
  text-decoration: line-through;
  color: #999;
}

.todo-item input[type="date"] {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-right: 12px;
  color: #666;
  font-size: 14px;
}

.due-date-input {
  min-width: 120px;
}

.delete-btn {
  background: none;
  border: none;
  color: #ff6b6b;
  font-size: 20px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.todo-item:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  background: #ff6b6b;
  color: white;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
}

.empty-state p {
  margin: 0;
  line-height: 1.5;
}

.quick-actions {
  width: 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
}

.action-btn {
  padding: 12px;
  border: none;
  border-radius: 8px;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: translateY(-2px);
}

.action-btn.primary {
  background-color: #a8e6cf;
  color: #333;
}

/* 开关按钮悬停效果 */
.action-btn.primary:hover {
  background-color: #95dfc4;
}

/* 全部/隐藏按钮悬停效果 */
.action-buttons button:nth-child(1):hover {
  background-color: #ffd1dc;
}

/* 清除全部按钮悬停效果 */
.action-buttons button:nth-child(2):hover {
  background-color: #ffb5b5;
}

/* 导出数据按钮悬停效果 */
.action-buttons button:nth-child(3):hover {
  background-color: #c8e6c9;
}

/* 导入数据按钮悬停效果 */
.action-buttons button:nth-child(4):hover {
  background-color: #c8e6c9;
}

/* 数据同步按钮悬停效果 */
.action-buttons button:nth-child(5):hover {
  background-color: #fff9c4;
}

/* 搜索框悬停效果 */
.search-input:hover {
  background-color: #f5f5f5 !important;
}

.search-input {
  text-align: left;
  background: white;
  padding: 12px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  color: #333;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.search-input:hover {
  border-color: #e0e0e0;
  background-color: #f5f5f5;
}

.search-input:focus {
  outline: none;
  border-color: #a8e6cf;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 删除旧的搜索框相关样式 */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .todo-container {
    margin: 0;
    border-radius: 0;
  }
  
  .main-content {
    flex-direction: column;
  }
  
  .quick-actions {
    width: 100%;
    padding: 10px;
  }
  
  .action-btn {
    flex: 1;
    min-width: 120px;
  }
  
  .action-buttons {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .due-date-input {
    min-width: 100px;
  }
  
  .drag-hint {
    display: none;
  }
  
  .status-bar {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style> 